/**
 * SongShareButton — canonical URL + native share sheet + quick-share links.
 * Public songs only. If the caller passes a non-published song we render a
 * "PUBLISH TO SHARE" hint instead of the share sheet.
 *
 * Analytics: fires POST /v1/analytics/share on each successful share (best-effort).
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Share2, Copy, Link2, Check, Facebook, Twitter, Linkedin, Mail, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

function canonicalUrl(slug) {
  const site = (typeof window !== "undefined" && (window.__SS_SITE_URL__ || window.location.origin)) || "https://songwritersquare.com";
  return `${site}/song/${slug}`;
}

function shareText(song) {
  return `Listen to ${song?.title || "this song"} on Songwriter Square.`;
}

async function trackShare(song, method) {
  try { await api.post("/v1/analytics/share", { song_id: song.id || song.slug, share_method: method }); }
  catch { /* best-effort */ }
}

export default function SongShareButton({ song, compact = false, label = "Share Song" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuW = 240;
    const left = Math.min(Math.max(8, r.right - menuW), window.innerWidth - menuW - 8);
    setPos({ left, top: r.bottom + 6 });
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  if (!song?.slug) return null;
  const published = song.status ? song.status === "published" : true; // public payloads omit status
  const isPrivate = !published;

  const url = canonicalUrl(song.slug);
  const text = shareText(song);

  const nativeShare = async () => {
    if (isPrivate) return;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: song.title, text, url });
        trackShare(song, "native");
        return;
      } catch { /* user cancelled */ }
    }
    setOpen((v) => !v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      trackShare(song, "copy_link");
    } catch { /* no-op */ }
  };

  const quickShare = (method, href) => {
    trackShare(song, method);
    window.open(href, "_blank", "noopener,noreferrer,width=680,height=560");
  };

  const links = [
    { id: "facebook",  Icon: Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { id: "x",         Icon: Twitter,  label: "X",        href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
    { id: "linkedin",  Icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
    { id: "whatsapp",  Icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}` },
    { id: "email",     Icon: Mail,     label: "Email",    href: `mailto:?subject=${encodeURIComponent(song.title || "Songwriter Square")}&body=${encodeURIComponent(text + "\n\n" + url)}` },
  ];

  return (
    <div className="relative inline-block" data-testid="song-share-wrapper">
      {isPrivate ? (
        <span className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-200 bg-amber-500/10 rounded-none h-9 px-3 text-[11px] uppercase tracking-widest font-display" data-testid="song-share-private-hint">
          Publish this song to share publicly
        </span>
      ) : (
        <button
          ref={btnRef}
          type="button"
          onClick={nativeShare}
          data-testid="song-share"
          className={`inline-flex items-center gap-1.5 border border-white/15 text-white/85 hover:border-gold hover:text-gold rounded-none uppercase tracking-widest font-display transition-colors ${
            compact ? "h-7 px-2 text-[10px]" : "h-9 px-3 text-xs"
          }`}
          aria-label="Share this song">
          <Share2 className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
          {!compact && <span>{label}</span>}
        </button>
      )}

      {open && !isPrivate && pos && typeof document !== "undefined" && createPortal(
        <div ref={menuRef} data-testid="song-share-menu"
             style={{ position: "fixed", left: pos.left, top: pos.top, width: 240 }}
             className="z-[9999] border border-gold/30 bg-black/95 backdrop-blur-md p-2 shadow-xl">
          <button type="button" onClick={copy} data-testid="song-share-copy"
                  className="w-full flex items-center gap-2 px-2 py-2 text-white/85 hover:text-gold hover:bg-gold/[0.06] text-xs uppercase tracking-widest">
            {copied ? <Check className="h-3.5 w-3.5 text-gold" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy Link"}
          </button>
          {links.map(({ id, Icon, label, href }) => (
            <button key={id} type="button" onClick={() => quickShare(id, href)} data-testid={`song-share-${id}`}
                    className="w-full flex items-center gap-2 px-2 py-2 text-white/85 hover:text-gold hover:bg-gold/[0.06] text-xs uppercase tracking-widest">
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
          <div className="mt-1 border-t border-white/10 pt-2 px-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40 min-w-0">
            <Link2 className="h-3 w-3 shrink-0" /> <span className="truncate min-w-0 flex-1">{url}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
